import React from "react";
import clsx from "clsx";

interface AlertProps {
  variant?: "info" | "warning" | "error" | "success";
  children: React.ReactNode;
  className?: string;
  /** Surface-only background (no outline border). Use on dark panels where a stroked alert feels heavy. */
  borderless?: boolean;
  "data-testid"?: string;
}

/** Background + text (no border tokens). */
const variantSurface = {
  info: "bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300",
  warning: "bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300",
  error: "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300",
  success: "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300",
} as const;

/** Border colors paired with {@link variantSurface}. */
const variantBorder = {
  info: "border border-blue-200 dark:border-blue-800",
  warning: "border border-yellow-200 dark:border-yellow-800",
  error: "border border-red-200 dark:border-red-800",
  success: "border border-green-200 dark:border-green-800",
} as const;

/**
 * Alert component for displaying important messages
 */
export function Alert({
  variant = "info",
  children,
  className = "",
  borderless = false,
  "data-testid": dataTestId,
}: AlertProps) {
  return (
    <div
      className={clsx(
        "p-4 rounded-lg text-sm",
        variantSurface[variant],
        !borderless && variantBorder[variant],
        className
      )}
      data-testid={dataTestId}
    >
      {children}
    </div>
  );
}
